import { HOC_MDXWrapper } from 'nextra/setup-page'
import { pageMap } from '/ui/docs-site/.next/static/chunks/nextra-page-map-.mjs'
/*@jsxRuntime automatic*/
/*@jsxImportSource react*/
/*-- dri: Alexa Kreizinger --*/
import {useMDXComponents as _provideComponents} from "nextra/mdx";
const title = "Parse";
const frontMatter = {
  "title": "Parse",
  "description": "Learn about the parse processing rule in Chronosphere Telemetry Pipeline."
};
import {Alert} from '@/components';
import Params, {useTOC as useTOC0} from '@/_partials/processing-rule-params.mdx';
import PipelineFlow, {useTOC as useTOC1} from '@/_partials/pipeline-flow-processing-rules.mdx';
export function useTOC(props) {
  return [...useTOC1(), {
    value: "Configuration parameters",
    id: "configuration-parameters",
    depth: 2
  }, ...useTOC0(), {
    value: "Example",
    id: "example",
    depth: 2
  }];
}
function MDXLayout(props) {
  const {toc = useTOC(props)} = props;
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    h1: "h1",
    h2: "h2",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ..._provideComponents(),
    ...props.components
  };
  return <>{}{"\n"}<_components.h1>{"Parse"}</_components.h1>{"\n"}<PipelineFlow />{"\n"}<_components.p>{"The "}<_components.em>{"parse"}</_components.em>{" processing rule uses a regular expression to search for values inside\na string and to assign a key to each value, then stores those key/value pairs\nin a structured object."}</_components.p>{"\n"}<Alert severity="info"><_components.p>{"This rule is a modified version of the "}<_components.a href="/pipeline-data/parsers">{"parsers"}</_components.a>{" feature."}</_components.p><_components.p>{"For a processing rule that performs a similar operation on embedded data already\nformatted as key/value pairs, see "}<_components.a href="/pipeline-data/processing-rules/extract-keys-values">{"extract keys/values"}</_components.a>{"."}</_components.p></Alert>{"\n"}<_components.h2 id={toc[0].id}>{toc[0].value}</_components.h2>{"\n"}<Params />{"\n"}<_components.table><_components.thead><_components.tr><_components.th>{"Name"}</_components.th><_components.th>{"Key"}</_components.th><_components.th>{"Description"}</_components.th><_components.th>{"Default"}</_components.th></_components.tr></_components.thead><_components.tbody><_components.tr><_components.td><_components.strong>{"Source key"}</_components.strong></_components.td><_components.td><_components.code>{"src"}</_components.code></_components.td><_components.td>{"Required. The key whose value contains data to parse."}</_components.td><_components.td><_components.em>{"none"}</_components.em></_components.td></_components.tr><_components.tr><_components.td><_components.strong>{"Destination key"}</_components.strong></_components.td><_components.td><_components.code>{"dst"}</_components.code></_components.td><_components.td>{"Required. The key of the object to store your structured key/value pairs. This rule can’t overwrite an existing key, so this value must be a unique name within your telemetry data."}</_components.td><_components.td><_components.em>{"none"}</_components.em></_components.td></_components.tr><_components.tr><_components.td><_components.strong>{"Regex"}</_components.strong></_components.td><_components.td><_components.code>{"regex"}</_components.code></_components.td><_components.td>{"Required. The regular expression for extracting values from the value of "}<_components.strong>{"Source key"}</_components.strong>{" and assigning keys to those values."}</_components.td><_components.td><_components.em>{"none"}</_components.em></_components.td></_components.tr><_components.tr><_components.td><_components.strong>{"Regex engine"}</_components.strong></_components.td><_components.td><_components.code>{"regexEngine"}</_components.code></_components.td><_components.td>{"Required. The "}<_components.a href="/pipeline-data/processing-rules#regex-engines">{"engine"}</_components.a>{" to parse your regular expression. Accepted values: "}<_components.code>{"GNU"}</_components.code>{", "}<_components.code>{"Oniguruma"}</_components.code>{", "}<_components.code>{"PCRE2"}</_components.code>{", "}<_components.code>{"POSIX"}</_components.code>{", "}<_components.code>{"TRE"}</_components.code>{"."}</_components.td><_components.td><_components.code>{"PCRE2"}</_components.code></_components.td></_components.tr><_components.tr><_components.td><_components.strong>{"Comment"}</_components.strong></_components.td><_components.td><_components.code>{"comment"}</_components.code></_components.td><_components.td>{"A custom note or description of the rule’s function. This text is displayed next to the rule’s name in the "}<_components.strong>{"Actions"}</_components.strong>{" list in the processing rules interface."}</_components.td><_components.td><_components.em>{"none"}</_components.em></_components.td></_components.tr></_components.tbody></_components.table>{"\n"}<_components.h2 id={toc[1].id}>{toc[1].value}</_components.h2>{"\n"}<_components.p>{"Using the parse processing rule lets you extract any data from a string and\nturn that data into parsable key/value pairs. You can then use these key/value\npairs in other processing rules or for general storage and analysis."}</_components.p>{"\n"}<_components.p>{"For example, given the following sample website logs:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="json" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"198.143.234.244 - - [18/Mar/2012:13:55:36 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"GET /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 200 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (compatible; MSIE 8.0; Windows NT 6.3; Trident/4.0)"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"66.216.63.42 - Lucille [18/Mar/2012:15:07:29 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"DELETE /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 200 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (iPhone; CPU iPhone OS 7_1_3; like Mac OS X) AppleWebKit/533.11 (KHTML, like Gecko)  Chrome/52.0.3189.332 Mobile Safari/536.3"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"70.137.97.47 - - [18/Mar/2012:19:21:04 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"GET /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 404 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (compatible; MSIE 8.0; Windows NT 6.3; Trident/4.0)"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"7.160.29.68 - Michael [19/Mar/2012:01:38:17 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"POST /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 200 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (U; Linux i585 x86_64; en-US) Gecko/20130401 Firefox/60.7"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"31.119.193.169 - - [19/Mar/2012:08:33:29 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"GET /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 200 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (iPad; CPU iPad OS 7_9_0 like Mac OS X) AppleWebKit/602.13 (KHTML, like Gecko)  Chrome/50.0.2327.233 Mobile Safari/534.2"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"A processing rule with the "}<_components.strong>{"Source key"}</_components.strong>{" value "}<_components.code>{"log"}</_components.code>{", the "}<_components.strong>{"Destination key"}</_components.strong>{"\nvalue "}<_components.code>{"parsed"}</_components.code>{", the "}<_components.strong>{"Regex"}</_components.strong>{" value\n"}<_components.code>{"^(?<host>[^ ]*) [^ ]* (?<user>[^ ]*) \\[(?<time>[^\\]]*)\\] \"(?<method>\\S+)(?: +(?<path>[^ ]*) +\\S*)?\" (?<code>[^ ]*) (?<size>[^ ]*)(?: \"(?<referer>[^\\\"]*)\" \"(?<agent>.*)\")?$"}</_components.code>{", and the "}<_components.strong>{"Regex engine"}</_components.strong>{"\nvalue "}<_components.code>{"PCRE2"}</_components.code>{" returns the following result:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="json" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"parsed"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"host"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"198.143.234.244\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"time"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"18/Mar/2012:13:55:36 -0700\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"path"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"/lease.pdf\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"200\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"user"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"-\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"agent"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Mozilla/5.0 (compatible; MSIE 8.0; Windows NT 6.3; Trident/4.0)\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"method"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GET\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"referer"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"bluth-homes.oc\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"size"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"5689972\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"198.143.234.244 - - [18/Mar/2012:13:55:36 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"GET /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 200 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (compatible; MSIE 8.0; Windows NT 6.3; Trident/4.0)"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"parsed"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"host"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"66.216.63.42\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"time"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"18/Mar/2012:15:07:29 -0700\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"path"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"/lease.pdf\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"200\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"user"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Lucille\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"agent"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Mozilla/5.0 (iPhone; CPU iPhone OS 7_1_3; like Mac OS X) AppleWebKit/533.11 (KHTML, like Gecko)  Chrome/52.0.3189.332 Mobile Safari/536.3\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"method"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"DELETE\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"referer"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"bluth-homes.oc\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"size"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"5689972\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"66.216.63.42 - Lucille [18/Mar/2012:15:07:29 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"DELETE /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 200 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (iPhone; CPU iPhone OS 7_1_3; like Mac OS X) AppleWebKit/533.11 (KHTML, like Gecko)  Chrome/52.0.3189.332 Mobile Safari/536.3"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"parsed"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"host"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"70.137.97.47\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"time"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"18/Mar/2012:19:21:04 -0700\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"path"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"/lease.pdf\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"404\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"user"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"-\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"agent"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Mozilla/5.0 (compatible; MSIE 8.0; Windows NT 6.3; Trident/4.0)\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"method"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GET\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"referer"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"bluth-homes.oc\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"size"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"5689972\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"70.137.97.47 - - [18/Mar/2012:19:21:04 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"GET /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 404 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (compatible; MSIE 8.0; Windows NT 6.3; Trident/4.0)"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"parsed"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"host"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"7.160.29.68\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"time"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"19/Mar/2012:01:38:17 -0700\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"path"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"/lease.pdf\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"200\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"user"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Michael\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"agent"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Mozilla/5.0 (U; Linux i585 x86_64; en-US) Gecko/20130401 Firefox/60.7\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"method"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"POST\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"referer"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"bluth-homes.oc\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"size"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"5689972\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"7.160.29.68 - Michael [19/Mar/2012:01:38:17 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"POST /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 200 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (U; Linux i585 x86_64; en-US) Gecko/20130401 Firefox/60.7"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"parsed"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"host"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"31.119.193.169\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"time"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"19/Mar/2012:08:33:29 -0700\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"path"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"/lease.pdf\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"200\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"user"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"-\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"agent"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Mozilla/5.0 (iPad; CPU iPad OS 7_9_0 like Mac OS X) AppleWebKit/602.13 (KHTML, like Gecko)  Chrome/50.0.2327.233 Mobile Safari/534.2\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"method"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GET\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"referer"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"bluth-homes.oc\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"size"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"5689972\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"31.119.193.169 - - [19/Mar/2012:08:33:29 -0700] "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"GET /lease.pdf HTTP/1.0"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 200 5689972 "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"bluth-homes.oc"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{" \\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"Mozilla/5.0 (iPad; CPU iPad OS 7_9_0 like Mac OS X) AppleWebKit/602.13 (KHTML, like Gecko)  Chrome/50.0.2327.233 Mobile Safari/534.2"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#22863A"
  }}>{"\\\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"This rule extracted values from the string stored in the "}<_components.code>{"log"}</_components.code>{" key, assigned a key\nto each value, then stored the resulting key/value pairs in a new structured object\nnamed "}<_components.code>{"parsed"}</_components.code>{"."}</_components.p></>;
}


export default HOC_MDXWrapper(
  MDXLayout,
  '/pipeline-data/processing-rules/parse',
  {"filePath":"pages/pipeline-data/processing-rules/parse.mdx",pageMap,frontMatter,title},
  typeof RemoteContent === 'undefined' ? useTOC : RemoteContent.useTOC
)